import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, Warning, Spinner } from "@phosphor-icons/react";
import api from "../lib/api";
import fireConfetti from "../lib/confetti";
import Seo from "../components/Seo";

/**
 * AdSuccess — landing page Stripe redirects to after a PPC-ad checkout.
 * Polls `/api/payments/status/{session_id}` until the payment is confirmed
 * (or times out), fires confetti, and bounces the user back to the feed.
 *
 * Polling schedule mirrors the existing app pattern used by Boost / Teams
 * Pass: 8 attempts, 1.5s spacing → up to 12s before giving up.
 */
export default function AdSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [attempts, setAttempts] = useState(0);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    let tries = 0;

    const poll = async () => {
      tries += 1;
      setAttempts(tries);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          if (!firedRef.current) {
            firedRef.current = true;
            fireConfetti();
          }
          setTimeout(() => nav("/app/home"), 2600);
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
      } catch {
        // Ignore transient errors — keep polling until we hit the cap.
      }
      if (tries >= 8) { setState("expired"); return; }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, nav]);

  return (
    <>
      <Seo title="Payment Confirmation — HigherVision"
           description="Finalising your sponsored reel." />
      <div className="min-h-screen flex items-center justify-center p-6"
           data-testid="ad-success-page">
        <div className="w-full max-w-sm rounded-2xl p-6 text-center relative"
             style={{
               background: "linear-gradient(180deg, #1a1206 0%, #050300 100%)",
               border: "1.5px solid #D4AF37",
               boxShadow: "0 24px 60px rgba(0,0,0,0.75), 0 0 30px rgba(255,215,0,0.25)",
             }}>
          {state === "polling" && (
            <div data-testid="ad-success-polling">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                   style={{ background: "linear-gradient(180deg, #FFE58A, #D4AF37 60%, #6b4d00)" }}>
                <Spinner size={26} weight="bold" color="#0a0703" className="animate-spin"/>
              </div>
              <div className="font-display text-xl text-[#FFD700]">Finalising your reel…</div>
              <div className="text-[11px] uppercase tracking-widest text-[#F2DC8C]/70 mt-2">
                Confirming payment · attempt {attempts}/8
              </div>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="ad-success-paid">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                   style={{ background: "linear-gradient(180deg, #FFE58A, #D4AF37 60%, #6b4d00)" }}>
                <CheckCircle size={26} weight="fill" color="#0a0703"/>
              </div>
              <div className="font-display text-2xl text-[#FFD700]">You&apos;re live 🎉</div>
              <div className="text-[12px] text-[#F2DC8C]/85 mt-2 leading-relaxed">
                Your sponsored reel is posted. Redirecting you back to the feed…
              </div>
              <Link to="/app/home" data-testid="ad-success-home"
                    className="mt-4 inline-block px-5 py-2 rounded-full font-display text-xs uppercase tracking-widest"
                    style={{
                      background: "linear-gradient(180deg, #FFE58A, #D4AF37 60%, #6b4d00)",
                      color: "#0a0703",
                    }}>
                Open feed →
              </Link>
            </div>
          )}
          {state === "expired" && (
            <div data-testid="ad-success-expired">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                   style={{ border: "1.5px solid #E86A92", background: "rgba(0,0,0,0.55)" }}>
                <Warning size={24} weight="fill" color="#E86A92"/>
              </div>
              <div className="font-display text-xl text-[#E86A92]">Payment not confirmed</div>
              <div className="text-[12px] text-[#F2DC8C]/70 mt-2 leading-relaxed">
                We didn&apos;t receive confirmation from Stripe in time. Your card wasn&apos;t
                charged, and your reel wasn&apos;t posted. Try posting again from Home.
              </div>
              <Link to="/app/home"
                    className="mt-4 inline-block px-5 py-2 rounded-full font-display text-xs uppercase tracking-widest"
                    style={{ border: "1px solid #D4AF37", color: "#FFD700" }}>
                Back to Home
              </Link>
            </div>
          )}
          {state === "error" && (
            <div data-testid="ad-success-error">
              <div className="font-display text-xl text-[#E86A92]">Missing session</div>
              <div className="text-[12px] text-[#F2DC8C]/70 mt-2">
                No checkout session was provided.
              </div>
              <Link to="/app/home" className="mt-4 inline-block px-5 py-2 rounded-full font-display text-xs uppercase tracking-widest"
                    style={{ border: "1px solid #D4AF37", color: "#FFD700" }}>
                Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
